import {useCallback} from 'react';
import {useRewardsBase} from './useRewardsBase';
import {
  createHandlePublish,
  createHandleNext,
  createHandlePrevious,
  createHandleDraft
} from './rewardHandlers';

/**
 * Hook for Create Reward flow
 * Handles create-specific logic: starts at step 1, name validation, etc.
 */
export const useRewardsCreate = () => {
  const common = useRewardsBase();
  const {
    currentStep,
    formData,
    validation,
    isLoading,
    validateCurrentStep,
    loadDraft,
    buildPayload,
    getDisableDraftFlag,
    navigate,
    dispatch,
    ...restCommon
  } = common;

  // Handle publishing (defined first so it can be used in handleNext)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePublish = useCallback(
    createHandlePublish({buildPayload, dispatch, navigate}),
    [buildPayload, navigate, dispatch]
  );

  // Handle next step with name validation for new rewards
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNext = useCallback(
    createHandleNext({
      currentStep,
      formData,
      validateCurrentStep,
      dispatch,
      handlePublish,
      shouldValidateName: true
    }),
    [currentStep, formData, validateCurrentStep, dispatch, handlePublish]
  );

  // Handle previous step
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePrevious = useCallback(
    createHandlePrevious({currentStep, dispatch}),
    [currentStep, dispatch]
  );

  // Handle draft saving with name validation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDraft = useCallback(
    createHandleDraft({
      currentStep,
      formData,
      buildPayload,
      loadDraft,
      dispatch,
      navigate,
      shouldValidateName: true
    }),
    [currentStep, formData, buildPayload, loadDraft, navigate, dispatch]
  );

  return {
    ...restCommon,
    currentStep,
    formData,
    validation,
    isLoading,
    handleNext,
    handlePrevious,
    handleDraft,
    handlePublish,
    disableDraft: getDisableDraftFlag()
  };
};
